import {Dispatch, FC, SetStateAction, useState} from 'react'
import clsx from 'clsx'
import {CustomInputs} from '../../../../../../../_metronic/helpers/components/CustomInputs'
import {Alert} from '../../../../../../../_metronic/helpers/components/Alert'
import {MAX_GOLD_QUANTITY_DECIMAL_PLACES} from './goldCostPricePreferences'

type ValueState = {
  value: string
  setValue: Dispatch<SetStateAction<string>>
}

type Props = {
  productName: string
  total: ValueState
  unitPrice: ValueState
  wagePercent: ValueState
  profitPercent: ValueState
  commissionPercent: ValueState
  quantityDecimalPlaces: ValueState
  isCalculating: boolean
  canContinue: boolean
  onChangeProduct: () => void
  onContinue: () => void
}

const GoldCostPriceEntryStep: FC<Props> = ({
  productName,
  total,
  unitPrice,
  wagePercent,
  profitPercent,
  commissionPercent,
  quantityDecimalPlaces,
  isCalculating,
  canContinue,
  onChangeProduct,
  onContinue,
}) => {
  const [showCalculationDetails, setShowCalculationDetails] = useState(false)
  const [showDecimalPlacesHelp, setShowDecimalPlacesHelp] = useState(false)
  const decimalPlaceItems = Array.from(
    {length: MAX_GOLD_QUANTITY_DECIMAL_PLACES + 1},
    (_, value) => ({value: value.toString(), label: value.toString()})
  )
  const decimalPlacesExample =
    quantityDecimalPlaces.value === '0'
      ? '1'
      : `1.${'23456789'.slice(0, Number(quantityDecimalPlaces.value))}`

  return (
    <div className='col-12'>
      <div className='rounded border border-dashed border-primary bg-light-primary p-4'>
        <div className='d-flex align-items-center justify-content-between gap-3'>
          <div>
            <span className='d-block fs-8 text-gray-600'>انتخاب کالا</span>
            <strong className='text-gray-900'>{productName || 'کالای انتخاب‌شده'}</strong>
          </div>
          <button type='button' className='btn btn-sm btn-light-primary' onClick={onChangeProduct}>
            تغییر کالا
          </button>
        </div>
      </div>

      <div className='row'>
        <div className='col-xl-6 col-lg-12 mt-5'>
          <CustomInputs
            type='currency'
            isRequired
            label='مبلغ نهایی فروش'
            stateValue={total}
          />
        </div>
        <div className='col-xl-6 col-lg-12 mt-5'>
          <CustomInputs
            type='currency'
            isRequired
            label='قیمت پایه'
            stateValue={unitPrice}
          />
        </div>
      </div>

      <div className='mt-5 rounded border border-dashed'>
        <button
          type='button'
          className='btn w-100 d-flex align-items-center justify-content-between px-5 py-4 text-start'
          aria-expanded={showCalculationDetails}
          aria-controls='gold-cost-price-calculation-details'
          onClick={() => setShowCalculationDetails((value) => !value)}
        >
          <span>
            <strong className='d-block text-gray-900'>جزئیات محاسبه</strong>
            <small className='text-gray-600'>درصدهای این ردیف را مشاهده یا ویرایش کنید.</small>
          </span>
          <i
            className={clsx(
              'las fs-2 text-primary',
              showCalculationDetails ? 'la-angle-up' : 'la-angle-down'
            )}
            aria-hidden='true'
          ></i>
        </button>

        {showCalculationDetails && (
          <div id='gold-cost-price-calculation-details' className='row px-4 pb-4'>
            <div className='col-xl-4 col-lg-12 mt-3'>
              <CustomInputs
                type='number'
                label='درصد اجرت ساخت'
                stateValue={wagePercent}
              />
            </div>
            <div className='col-xl-4 col-lg-12 mt-3'>
              <CustomInputs
                type='number'
                label='درصد سود فروشنده'
                stateValue={profitPercent}
              />
            </div>
            <div className='col-xl-4 col-lg-12 mt-3'>
              <CustomInputs
                type='number'
                label='درصد حق‌العمل'
                stateValue={commissionPercent}
              />
            </div>
            <div className='col-xl-4 col-lg-12 mt-3'>
              <CustomInputs
                type='select'
                label='تعداد ارقام اعشار میزان'
                labelAccessory={
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light-primary ms-2'
                    aria-label='راهنمای تعداد ارقام اعشار میزان'
                    title='راهنمای بیشتر'
                    onClick={() => setShowDecimalPlacesHelp(true)}
                  >
                    <i className='las la-question-circle fs-2' aria-hidden='true'></i>
                  </button>
                }
                items={decimalPlaceItems}
                stateValue={quantityDecimalPlaces}
                hint={`مثال: با انتخاب ${quantityDecimalPlaces.value}، میزان با حداکثر ${quantityDecimalPlaces.value} رقم اعشار محاسبه می‌شود؛ مانند ${decimalPlacesExample}.`}
              />
            </div>
          </div>
        )}
      </div>

      <button
        type='button'
        className='btn btn-success w-100 mt-7'
        disabled={!canContinue || isCalculating}
        onClick={onContinue}
      >
        {isCalculating ? (
          <>
            <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
            در حال محاسبه...
          </>
        ) : (
          'ادامه'
        )}
      </button>

      {showDecimalPlacesHelp && (
        <Alert
          type='info'
          title='راهنمای دقت میزان'
          body={
            <div className='text-start lh-lg'>
              <p>
                این تنظیم فقط تعداد ارقام اعشار میزان یا وزن محاسبه‌شده برای ردیف جاری را
                مشخص می‌کند.
              </p>
              <p>
                دقت بیشتر، میزان دقیق‌تری ایجاد می‌کند. در دقت‌های کمتر ممکن است برای برابر شدن
                مبلغ نهایی، تخفیف بیشتری محاسبه شود؛ مبلغ نهایی همچنان دقیق باقی می‌ماند.
              </p>
              <p className='mb-0'>آخرین مقدار استفاده‌شده برای همین کاربر و شرکت ذخیره می‌شود.</p>
            </div>
          }
          help={false}
          cancel={{text: 'متوجه شدم', fn: () => setShowDecimalPlacesHelp(false)}}
        />
      )}
    </div>
  )
}

export {GoldCostPriceEntryStep}
